import { useEffect, useState } from "react";
import { mdiSend, mdiFileDocumentEdit, mdiCancel, mdiEmailSyncOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useToggle } from "usehooks-ts";
import { useFundingRequestForm } from "./FundingRequestFormContext";
import { useFundingRequestUpdateAction } from "./FundingRequestUpdateActionContext";
import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import InvoiceDownloader from "./InvoiceDownloader";
import { useParams } from "react-router";
import { FR_DEFAULT_ATTACHMENTS_LIST, FR_GENERATED_ATTACHMENT } from "constants/haulpay";
import { getInvoiceForFRAPI } from "services/api/haulpay";
import { showErrorMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import Tooltip from "../../../../../../components/Tooltip";
import clsx from "clsx";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "../../../../../../helpers/constantTypes";
import { canBrokerDeclineApprovedSelfFinanceFR, canCancelCarrierPayment } from "./permissions";
import CarrierActions from "./CarrierActions";

const RESEND_HINT_ID = "resend-rate-confirmation-hint";

export default function RightActions() {
  const {
    sendInvoice,
    resendRateConfirmation,
    deleteFR,
    declineFR,
    cancelCarrierPayment,
    isGlobalDisabled,
    loading,
    selectedFR,
    methods,
    hasBOLDocument,
    isAssignedLoadsPage,
    showCarrierSubmitActions
  } = useFundingRequestForm();
  const {
    isAssignedLoadsMode,
    isCompletionStateActive,
    isButtonDisabled,
    updateBtnLabel,
    handleSubmitClick
  } = useFundingRequestUpdateAction();
  const { fundingRequestId } = useParams();
  const [invoiceFR, setInvoiceFR] = useState<FR_GENERATED_ATTACHMENT[]>([]);
  const [isSendInvoiceConfirmModalOpen, toggleSendInvoiceConfirmModal] = useToggle();
  const [isDeleteConfirmModalOpen, toggleDeleteConfirmModal] = useToggle();
  const [isDeclineConfirmModalOpen, toggleDeclineConfirmModal] = useToggle();
  const [isDeclining, setIsDeclining] = useState(false);
  const [isCancelCarrierConfirmModalOpen, toggleCancelCarrierConfirmModal] = useToggle();
  const [isCancellingCarrier, setIsCancellingCarrier] = useState(false);
  const [isResendRateConfirmationModalOpen, toggleResendRateConfirmationModal] = useToggle();
  const [isResendingRateConfirmation, setIsResendingRateConfirmation] = useState(false);
  const { isCarrier, isBroker } = useUserFeature();

  const selfFinanceContractTypes = [
    FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE,
    FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE,
    FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM
  ];
  const canDecline = canBrokerDeclineApprovedSelfFinanceFR({
    isBroker,
    status: selectedFR?.status,
    contractType: selectedFR?.contractType,
    canBrokerDecline: selectedFR?.canBrokerDecline
  });
  const canCancelCarrier = canCancelCarrierPayment({
    isBroker,
    status: selectedFR?.status,
    contractType: selectedFR?.contractType,
    canCancelCarrierPayment: selectedFR?.canCancelCarrierPayment
  });
  const isApprovedSelfFinanceBroker =
    isBroker &&
    selfFinanceContractTypes.includes(selectedFR?.contractType ?? "") &&
    ["Approved", "Remote Approved"].includes(selectedFR?.status ?? "");
  const showContactAdmin =
    isApprovedSelfFinanceBroker &&
    !canDecline &&
    !canCancelCarrier &&
    Boolean(selectedFR?.carrierFundingCommitted);

  // HP-8868: the resend only carries the funding request id, so the backend rebuilds the rate
  // confirmation from what is saved. Pending edits would be silently left out of it.
  const hasUnsavedChanges = Object.keys(methods.formState.dirtyFields ?? {}).length > 0;

  useEffect(() => {
    if (fundingRequestId) {
      setInvoiceFR(
        FR_DEFAULT_ATTACHMENTS_LIST.filter((item, index) => {
          if (isAssignedLoadsPage) {
            return index === 2;
          }
          if (isCarrier && item.value === "invoice_payment_profile") {
            return false;
          }
          return true;
        }).map((item) => {
          const fetchDocument =
            item.value === "invoice_payment_profile"
              ? () => getInvoiceForFRAPI(fundingRequestId, "invoice_payment_profile")
              : () => getInvoiceForFRAPI(fundingRequestId, item.value);
          return {
            ...item,
            fetchDocument
          };
        })
      );
    }
  }, [fundingRequestId, isCarrier]);

  if (loading) {
    return (
      <div className="bg-card border border-card rounded-lg p-3">
        <div className="animate-pulse flex flex-col gap-2">
          <div className="h-10 bg-foreground rounded-md"></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="h-10 bg-foreground rounded-md"></div>
            <div className="h-10 bg-foreground rounded-md"></div>
            <div className="h-10 bg-foreground rounded-md"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border border-card rounded-lg p-3">
        <div className="grid gap-2">
          <div className="flex w-full justify-end gap-2 mobile:hidden">
            {showCarrierSubmitActions ? (
              <div className="w-full">
                <CarrierActions tooltipPosition="down" tooltipContainerClassName="w-full" />
              </div>
            ) : isBroker ? (
              <Button
                variant="contained"
                className="w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white"
                onClick={handleSubmitClick}
                disabled={isButtonDisabled()}>
                <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                {updateBtnLabel()}
              </Button>
            ) : (
              <Tooltip
                message={`Upload a signed BOL to mark this request as ${
                  isAssignedLoadsPage ? "Delivered" : "completed"
                }`}
                position="down"
                tooltipContainerClassName="flex-1"
                disabled={hasBOLDocument || !isAssignedLoadsMode}>
                <Button
                  type="button"
                  className={clsx(
                    "w-full bg-[rgb(var(--primary))] hover:bg-[rgb(var(--primary-dark))] text-white",
                    "!ml-auto",
                    isCompletionStateActive ? "!bg-green-500 !text-white" : ""
                  )}
                  onClick={handleSubmitClick}
                  disabled={isButtonDisabled()}>
                  <Icon path={mdiFileDocumentEdit} size={1} className="mr-5" />
                  {updateBtnLabel()}
                </Button>
              </Tooltip>
            )}
          </div>
          <div className={`grid ${isCarrier ? "grid-cols-2" : "grid-cols-3"} gap-3`}>
            {invoiceFR.map((item) => (
              <InvoiceDownloader
                key={item.id}
                label={item.label}
                fetchDocument={item.fetchDocument}
              />
            ))}
          </div>
        </div>
        {!isCarrier && (
          <>
            <p className="text-center text-red-500 mt-2">
              Times Invoice Sent: {selectedFR?.invoiceSentCount}
            </p>
            <div className="mt-2 flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 hover:bg-[rgb(var(--primary))] hover:text-white flex items-center justify-center"
                onClick={toggleSendInvoiceConfirmModal}
                disabled={isGlobalDisabled}>
                <Icon path={mdiSend} size={1} className="mr-5" />
                Send Invoice Now
              </Button>
            </div>
            {/* HP-8868: the server decides who may resend, so this renders only when it says so */}
            {selectedFR?.canResendRateConfirmation && (
              <div className="mt-2 flex w-full flex-col gap-1">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full hover:bg-[rgb(var(--primary))] hover:text-white flex items-center justify-center"
                  onClick={toggleResendRateConfirmationModal}
                  aria-describedby={hasUnsavedChanges ? RESEND_HINT_ID : undefined}
                  disabled={isResendingRateConfirmation || hasUnsavedChanges}>
                  <Icon path={mdiEmailSyncOutline} size={1} className="mr-5" />
                  Resend Rate Confirmation
                </Button>
                {/* A visible message rather than a tooltip: a disabled button cannot take focus,
                    so a hover-only tooltip leaves keyboard and screen reader users with no reason. */}
                {hasUnsavedChanges && (
                  <p id={RESEND_HINT_ID} className="text-center text-sm text-placeholder">
                    Save your changes first — the rate confirmation is built from the saved amount.
                  </p>
                )}
              </div>
            )}
            {canCancelCarrier && (
              <div className="mt-2 flex w-full justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 hover:bg-[rgb(var(--primary))] hover:text-white flex items-center justify-center"
                  onClick={toggleCancelCarrierConfirmModal}
                  disabled={isCancellingCarrier}>
                  <Icon path={mdiCancel} size={1} className="mr-5" />
                  Cancel Carrier Payment
                </Button>
              </div>
            )}
            {canDecline && (
              <div className="mt-2 flex w-full justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 !border-red-500 !text-red-500 hover:!bg-red-500 hover:!text-white flex items-center justify-center"
                  onClick={toggleDeclineConfirmModal}
                  disabled={isDeclining}>
                  <Icon path={mdiCancel} size={1} className="mr-5" />
                  Decline Funding Request
                </Button>
              </div>
            )}
            {showContactAdmin && (
              <p className="text-center text-placeholder mt-2 text-sm">
                The carrier has already been funded on this request, so it can no longer be declined
                here. Please contact an admin to reverse the payment.
              </p>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        toggleConfirmModal={toggleSendInvoiceConfirmModal}
        isConfirmModalOpen={isSendInvoiceConfirmModalOpen}
        content="Please make sure that any attached documents that are required for invoicing have completed processing for upload before confirming to send."
        labelOKButton="Send"
        onOK={async () => {
          try {
            await sendInvoice();
            toggleSendInvoiceConfirmModal();
          } catch {
            // empty block
          }
        }}
      />

      <ConfirmationModal
        toggleConfirmModal={toggleDeleteConfirmModal}
        isConfirmModalOpen={isDeleteConfirmModalOpen}
        content="Are you sure you want to delete this funding request? This action cannot be undone."
        labelOKButton="Delete"
        onOK={deleteFR}
      />

      <ConfirmationModal
        title="Decline Funding Request"
        toggleConfirmModal={toggleDeclineConfirmModal}
        isConfirmModalOpen={isDeclineConfirmModalOpen}
        isLoading={isDeclining}
        content={
          <span className="block text-left">
            Declining this funding request will cancel the carrier payment, void the related
            charges, and close the debtor invoice. Once declined, it cannot be re-approved or
            edited. This action cannot be undone.
          </span>
        }
        labelOKButton="Decline"
        onOK={async () => {
          try {
            setIsDeclining(true);
            await declineFR();
            toggleDeclineConfirmModal();
          } catch {
            // keep the modal open on failure so the broker can retry
          } finally {
            setIsDeclining(false);
          }
        }}
      />

      <ConfirmationModal
        title="Resend Rate Confirmation"
        toggleConfirmModal={toggleResendRateConfirmationModal}
        isConfirmModalOpen={isResendRateConfirmationModalOpen}
        isLoading={isResendingRateConfirmation}
        content={
          <span className="block text-left">
            This emails {selectedFR?.paymentProfile?.companyName ?? "the carrier"} a fresh rate
            confirmation using the funding request&apos;s saved carrier pay amount.
          </span>
        }
        labelOKButton="Resend"
        onOK={async () => {
          // Re-checked here as well as on the button: the form can be edited while the modal is up.
          if (hasUnsavedChanges) {
            showErrorMessage("Save your pending changes before resending the rate confirmation.");
            return;
          }
          try {
            setIsResendingRateConfirmation(true);
            const didSend = await resendRateConfirmation();
            if (didSend) {
              toggleResendRateConfirmationModal();
            }
          } finally {
            setIsResendingRateConfirmation(false);
          }
        }}
      />

      <ConfirmationModal
        title="Cancel Carrier Payment"
        toggleConfirmModal={toggleCancelCarrierConfirmModal}
        isConfirmModalOpen={isCancelCarrierConfirmModalOpen}
        isLoading={isCancellingCarrier}
        content={
          <span className="block text-left">
            This cancels the carrier payment and reverses the carrier advance. The funding request
            stays approved and the debtor invoice remains open for you to collect off-platform. The
            self-finance fee still applies.
          </span>
        }
        labelOKButton="Cancel Carrier Payment"
        onOK={async () => {
          try {
            setIsCancellingCarrier(true);
            await cancelCarrierPayment();
            toggleCancelCarrierConfirmModal();
          } catch {
            // keep the modal open on failure so the broker can retry
          } finally {
            setIsCancellingCarrier(false);
          }
        }}
      />
    </>
  );
}
